import React, { useState, useMemo, useEffect, useRef } from 'react';
import { isMobile } from '../../../utils/device';
import QuestionnaireSection from './components/QuestionnaireSection';
import ResultsOverview from './components/ResultsOverview';
import RegistrationForm from './components/RegistrationForm';
import { useQuestionnaire } from './hooks/useQuestionnaire';
import { Assessment } from './types';
import { isUserRegistered, storeAssessmentData, QuestionnaireType } from '@/services/hubspotService';
import { useTheme } from '@/contexts/ThemeContext';

const PrivacyActQuestionnaire: React.FC = () => {
  // Force light mode for questionnaire
  const { mode, setMode } = useTheme();
  const originalModeRef = useRef(mode);
  
  // In a real implementation, you would get the tenantId from context or props
  const tenantId = 'current-tenant-id';
  const { categories, responses, handleAnswerChange, assessment } = useQuestionnaire(tenantId);
  
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showRegistration, setShowRegistration] = useState(true);
  const [validationError, setValidationError] = useState('');

  // Force light mode without infinite loop
  const hasStoredOriginalMode = useRef(false);
  
  useEffect(() => {
    // Store original mode only once on first render
    if (!hasStoredOriginalMode.current) {
      originalModeRef.current = mode;
      hasStoredOriginalMode.current = true;
      // Store original mode in sessionStorage for reliable restoration
      sessionStorage.setItem('original-theme-mode', mode);
    }
    
    // Only force light mode if not already light
    if (mode !== 'light') {
      setMode('light');
    }
  }, [mode]); // Only depend on mode, not setMode

  // Cleanup: restore original theme when component unmounts
  useEffect(() => {
    return () => {
      setMode(originalModeRef.current);
      // Clear sessionStorage when leaving questionnaire
      sessionStorage.removeItem('original-theme-mode');
    };
  }, []); // Only run cleanup on unmount
  
  // Check if user is already registered
  useEffect(() => {
    const userRegistered = isUserRegistered();
    setShowRegistration(!userRegistered);
  }, []);

  // Navigate to the next category
  const handleNext = () => {
    // Get current category
    const current = categories[currentCategoryIndex];
    
    // Check if the user has answered the question
    const currentResponse = responses[current.id];
    
    if (!currentResponse?.answer) {
      setValidationError('Please answer the question before proceeding');
      return;
    }
    
    // Clear any existing validation errors
    setValidationError('');
    
    // Proceed to next category
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex(currentCategoryIndex + 1);
      // Scroll to top of the questionnaire
      window.scrollTo(0, 0);
    }
  };

  // Navigate to the previous category
  const handlePrevious = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(currentCategoryIndex - 1);
      // Scroll to top of the questionnaire
      window.scrollTo(0, 0);
    }
  };
  
  // Handle registration completion
  const handleRegistrationComplete = () => {
    setShowRegistration(false);
  };
  
  // Handle form submission and send to HubSpot
  const handleSubmitAssessment = () => {
    // Get current category (the final one)
    const current = categories[currentCategoryIndex];
    
    // Check if the user has answered the question
    const currentResponse = responses[current.id];
    
    if (!currentResponse?.answer) {
      setValidationError('Please answer the question before submitting');
      return;
    }
    
    // Clear any existing validation errors
    setValidationError('');
    
    // Mark as submitted
    setIsSubmitted(true);
    
    // If we have assessment data, store it for HubSpot
    if (transformedAssessment) {
      // Store assessment results in HubSpot via our service
      setTimeout(() => {
        storeAssessmentData(transformedAssessment, undefined, QuestionnaireType.PRIVACY_ACT);
        
        // TODO: DURING ONBOARDING WE NEED TO MIGRATE QUESTION RESULTS FROM HUBSPOT TO FIREBASE
        // This should be implemented as part of the customer onboarding process
        console.log('[Privacy Act] Assessment completed and stored with HubSpot. Will need migration during onboarding.');
      }, 1000);
    }
  };
  
  // Transform the PrivacyActAssessment to our Assessment type for the UI components
  const transformedAssessment = useMemo((): Assessment | null => {
    if (!assessment) return null;
    
    // Create compliance levels map
    const complianceLevels: Record<string, number> = {};
    Object.entries(assessment.responses).forEach(([categoryId, response]) => {
      complianceLevels[categoryId] = response.complianceLevel;
    });
    
    return {
      id: assessment.id || 'temp-id',
      tenantId: assessment.tenantId,
      completed: isSubmitted,
      createdAt: assessment.timestamp.toISOString(),
      updatedAt: assessment.timestamp.toISOString(),
      completedAt: isSubmitted ? new Date().toISOString() : undefined,
      maturityLevels: complianceLevels,
      overallMaturityLevel: Math.min(...Object.values(complianceLevels)),
      categories: Object.values(assessment.responses).map(response => ({
        categoryId: response.categoryId,
        responses: [{ answer: response.answer }], // Preserve the raw Yes/No/Unsure answer
        maturityLevel: response.complianceLevel
      })),
      // Include questionnaire version information
      questionnaireVersion: {
        version: assessment.questionnaireVersion,
        releaseDate: '2025-11-26',
        description: 'Australian Privacy Act and APPs Self-Assessment'
      }
    };
  }, [assessment, isSubmitted]);

  // Get current category
  const currentCategory = categories[currentCategoryIndex];

  // Show registration form if needed
  if (showRegistration) {
    return (
      <RegistrationForm
        open={showRegistration}
        onComplete={handleRegistrationComplete}
        onClose={() => setShowRegistration(false)}
      />
    );
  }
  
  const isMobileDevice = isMobile();
  
  return (
    <div 
      className={`w-full max-w-screen-xl mx-auto px-4 overflow-x-hidden overflow-y-auto ${isMobileDevice ? '' : ''}`} 
      style={{ 
        WebkitOverflowScrolling: 'touch', 
        height: '-webkit-fill-available',
        ...(isMobileDevice ? { paddingBottom: '40px' } : {})
      }}
    >
      <div className="glass-card w-full max-w-2xl mx-auto px-4 py-8 overflow-visible rounded-lg">

      <div id="questionnaire-title" className="mb-10">
        <h1 className="text-3xl font-bold text-foreground mb-4">Australian Privacy Act and APPs Self-Assessment</h1>
        <p className="text-muted-foreground">
          This questionnaire helps you check whether your organisation meets the requirements of the Privacy Act 1988, 
          specifically the 13 Australian Privacy Principles (APPs). The APPs are the core rules that govern standards, 
          rights and obligations around the collection, use, storage disclosure, and correction of personal information.
        </p>
        <p className="text-muted-foreground mt-2">
          By working through each section, you'll gain a clear picture of your current maturity and compliance level, 
          identify gaps, and highlight areas that may need improvement. The results will provide a practical starting 
          point for strengthening your data governance and meeting legal obligations.
        </p>
      </div>
      
      {isSubmitted && transformedAssessment && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-foreground mb-4">Your Privacy Act Compliance Snapshot</h2>
          <ResultsOverview assessment={transformedAssessment} />
        </div>
      )}

      {!isSubmitted && (
        <>
          {/* Progress indicator */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">
                APP {currentCategoryIndex + 1} of {categories.length}
              </span>
              <span className="text-sm font-medium">
                {Math.round(((currentCategoryIndex + 1) / categories.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-primary h-2.5 rounded-full" 
                style={{ width: `${((currentCategoryIndex + 1) / categories.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Assessment questionnaire - only shown if not completed */}
          {currentCategory && (
            <QuestionnaireSection
              category={currentCategory}
              responses={responses[currentCategory.id]}
              onAnswerChange={handleAnswerChange}
            />
          )}

          {/* Validation error message */}
          {validationError && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive text-destructive-foreground rounded">
              <p className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationError}
              </p>
            </div>
          )}

          {/* Navigation buttons */}
          <div className={`flex justify-between mt-8 ${isMobileDevice ? '' : ''}`}>
            <button
              onClick={handlePrevious}
              disabled={currentCategoryIndex === 0}
              className="px-4 py-3 bg-secondary text-secondary-foreground rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            {currentCategoryIndex === categories.length - 1 ? (
              <button
                onClick={handleSubmitAssessment}
                className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Submit Assessment
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-700"
              >
                Next
              </button>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default PrivacyActQuestionnaire;
